/**
 * consent — turn a decided page into the body `/authorize/approve` expects.
 *
 * The page thinks in flat (resource, verb, schema) triples because that is what
 * a person can read. The backend stores an access blob. This is the one place
 * that translation happens, so the shape the user saw and the shape the server
 * stores cannot drift apart in two different files.
 *
 * WHAT IS DELIBERATELY NOT HERE: anything that widens. This only ever emits
 * grants the caller says are ON, and scope_type defaults to the RESTRICTIVE
 * `own` — an omission must never become "any", because the failure mode of a
 * consent builder is granting more than was agreed, silently.
 */
import { isScoped } from '../../pages/authorization/grantAxis';

/**
 * Granted triples → the canonical access blob.
 *
 * @param {Array}    grants   the ON grants, as returned by /authorize/preview
 * @param {Function} scopeFor (verb) => 'own' | 'any'   — per scope-bearing verb
 */
/**
 * The {fields, filter} mask a grant carries, or {} for an unrestricted one.
 *
 * `/authorize/preview` emits these per row (see `_triple` in preview.py). This
 * used to write `{}` unconditionally, which meant an app that asked for two
 * relationship types — or three fields of a schema — was GRANTED the whole
 * schema the moment someone approved. The narrower thing was displayed and the
 * wider thing was stored.
 *
 * Empty axes are dropped rather than sent as empty containers: `{fields: []}`
 * would read as "no fields at all" to apply_mask, which is not what an absent
 * projection means.
 */
function maskOf(g) {
  const mask = {};
  if (Array.isArray(g.fields) && g.fields.length) mask.fields = [...g.fields];
  if (g.filter && Object.keys(g.filter).length) mask.filter = { ...g.filter };
  return mask;
}

/**
 * The grant when NOBODY ASKED — a friend share, where the realm is the grant.
 *
 * An app arrives with a DLR, so `/authorize/preview` fills `ask.grants` and the
 * page builds consent from it. A person does not: `preview.py` gates both `items`
 * and `grants` on `app_row`, so a friend share comes back with `ask.grants: []`.
 * Building the consent from that empty list produced `{}` — deny-all — so a
 * "Full Access" share stored NO NODE ACCESS and the friend saw nothing. The page
 * said "the realm is the grant" everywhere except the one place that mattered.
 *
 * Unrestricted per verb, deliberately: effective access is consent ∩ ceiling, so
 * the realm already decides which schemas are reachable. Naming schemas here
 * would narrow the grant a second time, to whatever the inventory happened to
 * hold at save.
 */
export function grantsFromRealms(realms = []) {
  const seen = new Set();
  const out = [];
  for (const r of realms) {
    for (const v of r?.verbs || []) {
      if (seen.has(v)) continue;
      seen.add(v);
      const i = v.indexOf(':');
      if (i < 1) continue;
      const resource = v.slice(0, i);
      const verb = v.slice(i + 1);
      // `all_schemas` is the nodes axis only; the others have no schema axis at
      // all and an empty scope already reads as unrestricted for them.
      out.push(resource === 'nodes'
        ? { resource, verb, all_schemas: true }
        : { resource, verb });
    }
  }
  return out;
}

export function buildConsentBlob(grants = [], { scopeFor, links = false } = {}) {
  const blob = {};
  for (const g of grants) {
    if (!g?.resource || !g?.verb) continue;
    const res = (blob[g.resource] = blob[g.resource] || {});
    const scope = (res[g.verb] = res[g.verb] || {});

    if (g.all_schemas) {
      // Unrestricted for this verb: an explicit schema list would NARROW it,
      // and a later grant must not silently shrink an earlier one.
      delete scope.schemas;
      scope.__unrestricted = true;
    } else if (!scope.__unrestricted && g.schema_id) {
      scope.schemas = scope.schemas || {};
      const key = String(g.schema_id);
      const mask = maskOf(g);
      const prev = scope.schemas[key];
      // NEVER let a later unmasked row erase an earlier mask. Two rows can name
      // the same (verb, schema) — the ask and a rehydrated grant, say — and if
      // the unmasked one landed second, a narrowed grant would widen to the
      // whole schema on save. Same invariant as `__unrestricted` above: an
      // omission must not become "everything".
      scope.schemas[key] = (prev && Object.keys(prev).length && !Object.keys(mask).length)
        ? prev : mask;
    }

    if (isScoped(g)) {
      // Write `own` ONLY when the person chose it. Coercing omission to `own`
      // meant every grant through this page carried the narrow setting whether
      // or not anyone touched the toggle — and the day the backend started
      // consulting it, every re-authorized app narrowed itself and broke
      // (Caliber, Missions: creates 403'd against their own schemas). Absent
      // scope_type has always meant unrestricted; the blob says what was
      // decided, not what a form defaulted to.
      const v = scopeFor?.(g.verb);
      if (v === 'own') scope.scope_type = 'own';
    }
  }
  // THE SHAPE OF THE GRAPH IS ITS OWN GRANT, and it rides on the read scope
  // rather than on a row of its own — `include_refs`, the successor to the
  // retired `relationships:read` for wildcard grants. It is not derivable from
  // the rows: a bare `nodes:read` scope is SILENT about references and is
  // enforced as "mask them", so a blob built from the rows alone can never say
  // yes to it. Written only where the caller passes `links` — the page passes
  // it when the app asked AND a chosen realm carries it, which is the same AND
  // enforcement applies.
  if (links && blob.nodes?.read) blob.nodes.read.include_refs = true;
  // strip the marker; `{}` is how "unrestricted" is spelled
  for (const verbs of Object.values(blob)) {
    for (const scope of Object.values(verbs)) delete scope.__unrestricted;
  }
  return blob;
}

/**
 * The full approve body.
 *
 * `privacy_realm_id` is required by the endpoint and is never inferred here —
 * a submit with no realm is a bug the caller must not be able to paper over,
 * so this throws rather than guessing.
 */
export function buildApproveBody({
  clientId, realmId, realmIds, grants, scopeFor, links, expiresAt, groupId, redirectUri, toYaml,
  encUserPrivkey, faceId, showAllFaces, placement,
}) {
  if (!clientId) throw new Error('consent: clientId is required');
  // A REALM IS REQUIRED UNLESS ONE IS BEING MINTED. Naming no realm used to be a
  // bug the caller must not be able to paper over; with `mint_shared_realm` it is
  // the narrowest grant there is — the app's ceiling becomes the realm made for
  // it, and none of the person's are attached. The server mints it before the
  // grant, because the primary-realm column cannot be null.
  if (!realmId && !placement?.mintSharedRealm) {
    throw new Error('consent: a realm must be chosen before approving');
  }

  const blob = buildConsentBlob(grants, { scopeFor, links });
  const all = (realmIds && realmIds.length) ? realmIds : (realmId ? [realmId] : []);
  return {
    client_id: clientId,
    // Both: the endpoint accepts either, and `privacy_realm_id` remains the
    // primary/display pointer while the union is what actually grants.
    ...(realmId ? { privacy_realm_id: realmId } : {}),
    ...(all.length > 1 ? { privacy_realm_ids: all } : {}),
    consent_yaml: toYaml ? toYaml(blob) : blob,
    ...(expiresAt ? { expires_at: expiresAt } : {}),
    ...(groupId ? { group_id: groupId } : {}),
    ...(redirectUri ? { redirect_uri: redirectUri } : {}),
    // WHO YOU APPEAR AS, when this screen was the one that settled it. Omitted means
    // nobody overrode the ladder — the realms answer, then your default — which is
    // why it is absent rather than null when untouched.
    ...(faceId ? { profile_node_id: faceId } : {}),
    ...(showAllFaces ? { shows_all_identities: true } : {}),
    // The owner's private key, encrypted TO this app, so it can read encrypted
    // fields. Absent for apps that do not use encryption; without it an app that
    // does is refused with ENCRYPTION_KEY_REQUIRED.
    ...(encUserPrivkey ? { enc_user_privkey: encUserPrivkey } : {}),
    // WHERE THE ROWS NO PICKED REALM REACHES ARE ALLOWED TO GO.
    //
    // SILENCE IS A REFUSAL on this endpoint, deliberately: with neither key the
    // server answers REALM_TOO_NARROW exactly as it always did, so a client that
    // has never heard of placement cannot have a realm created on its behalf.
    // The page sends these only when the person chose a home for those rows.
    ...(placement?.attachRealmId ? { attach_realm_id: placement.attachRealmId } : {}),
    // GROWING A REALM IS A DIFFERENT ACT FROM ATTACHING ONE, and the difference
    // is the whole reason this key exists: the realm named here is already
    // attached and does not cover the rows, so it is being widened until it does.
    // That edits an object other grants are attached to, which is why the page
    // asks who else gains before it sends this.
    ...(placement?.widenRealmId ? { widen_realm_id: placement.widenRealmId } : {}),
    // AND WHETHER THE PERSON WAS SHOWN WHAT UNRESTRICTED MEANS.
    //
    // A residue with no type limit does not grow the realm by two schemas, it
    // makes the realm unrestricted — for this grant and for every other one
    // attached to it, including grants made later. The route refuses that unless
    // this says otherwise, and it is not a preference: the page sets it only when
    // it has fetched `preview-widening` and put the grants that gain on screen.
    // A caller that cannot do that sends nothing and gets the old refusal, which
    // is the whole reason the key exists rather than the server simply allowing it.
    ...(placement?.widenUnbounded ? { widen_unbounded: true } : {}),
    ...(placement?.mintSharedRealm ? { mint_shared_realm: true } : {}),
  };
}

/**
 * The body `/authorize/sub-entity` expects.
 *
 * Deliberately NOT a variant of `buildApproveBody`: the two routes disagree on
 * the one field that matters. Approve takes `consent_yaml` (a YAML string);
 * this one reads `consent` (the blob itself). Sending the wrong key is accepted
 * without complaint and grants nothing — the same silent-empty-consent shape
 * that once stripped a Full Access share down to no node access.
 *
 * `clientId` is the PARENT APP's client_id. An agent has none of its own, and
 * the route resolves its parent by that value, so a missing one has to throw
 * here rather than send something that merely looks like an id.
 */
export function buildSubEntityBody({
  clientId, subEntityKey, realmId, realmIds, grants, scopeFor, links, expiresAt, groupId,
}) {
  if (!clientId) throw new Error('consent: the parent app client_id is required');
  if (!subEntityKey) throw new Error('consent: sub_entity_key is required');
  if (!realmId) throw new Error('consent: a realm must be chosen before approving');

  const blob = buildConsentBlob(grants, { scopeFor, links });
  const all = (realmIds && realmIds.length) ? realmIds : [realmId];
  return {
    client_id: clientId,
    sub_entity_key: subEntityKey,
    privacy_realm_id: realmId,
    ...(all.length > 1 ? { privacy_realm_ids: all } : {}),
    // Omitted when empty, never sent as {}. An empty-but-present consent is
    // read as "narrow to nothing"; absent means "inherit the realm ceiling",
    // which is what choosing no narrowing actually means.
    ...(Object.keys(blob).length ? { consent: blob } : {}),
    // ALWAYS SENT, null included — the same rule as a share. Omitting it for
    // "Never expires" left an expired agent's old date in place: reauthorizing
    // reported success and the agent stayed expired.
    expires_at: expiresAt || null,
    ...(groupId ? { group_id: groupId } : {}),
  };
}

/** Expiry choices, in the vocabulary the page uses. */
export const EXPIRY = [
  { value: '7d', label: 'In 7 days', days: 7 },
  { value: '30d', label: 'In 30 days', days: 30 },
  { value: '90d', label: 'In 3 months', days: 90 },
  { value: 'never', label: 'Never expires', days: null },
];

/**
 * The expiries it makes sense to offer for a given price.
 *
 * ON A SUBSCRIPTION, AN EXPIRY SHORTER THAN THE BILLING PERIOD IS A TRAP. Pick "7 days"
 * on a monthly plan and access stops after a week while the charges continue — the
 * person has paid for a month and time-limited themselves to a quarter of it, from a
 * control that says nothing about money.
 *
 * The two are genuinely different questions: expiry is "how long may this app reach my
 * graph", billing is "how long have I paid for". They only conflict when the first is
 * shorter than the second, so that is the only case filtered — nobody is prevented from
 * choosing a SHORTER relationship than their subscription on a one-off purchase, or
 * from ending a subscription early by revoking, which stops the billing too.
 */
// Accepting a follow, joining a group and setting a public page take no expiry:
// their routes never read one, so a picker there chose "30 days" and wrote never.
// Nor does an app's agent: its grant lives as long as the app's (the model's
// rule, app/models/privacy.py), so a date here was one nothing would keep.
export const UNDATED_KINDS = new Set(['follow', 'group', 'public', 'sub_entity']);

export function expiryOptionsFor(pricingDisplay, interval) {
  const isSubscription = pricingDisplay?.model === 'subscription';
  if (!isSubscription) return EXPIRY;
  // An annual plan makes every fixed option too short; only "never" is coherent.
  const minDays = interval === 'annual' ? 365 : 30;
  return EXPIRY.filter(o => o.days == null || o.days >= minDays);
}

export function expiresAtFrom(value, now) {
  const opt = EXPIRY.find((o) => o.value === value);
  if (!opt || opt.days == null) return null;
  const base = now instanceof Date ? new Date(now.getTime()) : new Date();
  base.setDate(base.getDate() + opt.days);
  return base.toISOString();
}

/**
 * Is a stored expiry still ahead of us?
 *
 * A lapsed date is not something anyone can choose to KEEP. Offering it back
 * renews a grant into a term that has already run out — the page reports a
 * successful re-authorization and the grant behaves like none at all, because
 * `get_active_app_ep` reads an expired EP as absent.
 *
 * SHARED ON PURPOSE, not duplicated. `useGrant` decides the expiry VALUE and
 * `GrantRoute` renders the OPTIONS. If those two ever disagreed about what is
 * keepable, the select would hold 'keep' with no matching <option> and render
 * blank.
 */
export function canKeep(iso, now = new Date()) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t > now.getTime();
}

/**
 * View or edit, for a node share — ONE expression, used by the submit below and by
 * the sentence the page shows about it.
 *
 * They were separate: the submit read the checked grants and the page's copy said
 * "will be able to read this note" unconditionally, so an edit share promised the
 * reader less than it granted. A sentence about a decision and the decision itself
 * must come from the same place or they drift, and only one of them is enforced.
 */
export function nodeShareAccess(grants) {
  return (grants || []).some((g) => g?.resource === 'nodes' && g?.verb === 'edit')
    ? 'edit' : 'view';
}

/**
 * Where a decision goes, per counterparty.
 *
 * The page rendered every edge and could only SUBMIT one of them: `onApprove`
 * built an app-shaped body unconditionally, so a friend share or a group join
 * threw on `clientId is required` before it sent anything. Rendering all 22 was
 * never the hard half.
 *
 * Each kind has its own endpoint because each is a different act — a share is
 * not an authorization is not an invitation reply. This maps them, and refuses
 * loudly for anything it does not know rather than guessing at `/authorize`.
 */
export function submitPlan({ preview, realmIds, grants, scopeFor, links, expiresAt,
                             groupId, redirectUri, toYaml, encUserPrivkey, faceId,
                             showAllFaces, placement, extra = {} }) {
  const kind = preview?.requester?.kind;
  const who = preview?.requester || {};
  const realmId = realmIds?.[0] || null;

  switch (kind) {
    case 'app':
      // A RUNTIME ASK IS ANSWERED, NOT RE-GRANTED. `/authorize/approve` is a
      // total re-statement of the grant — realms, expiry, the whole consent —
      // and it knows nothing of the batch, so the rows the app is waiting on
      // would stay pending forever while the person was told they had said
      // yes. `/authorize/expand` widens the standing grant by exactly the
      // batch's items and settles them. No realm goes with it: the server
      // files the widening inside the realms the grant already has when they
      // cover it, else in the pair's own shared realm (see preview.py's
      // `exact` lock) — attaching one of the person's other realms would
      // raise the app's ceiling by everything in it, which is more than was
      // asked.
      if (who.runtime?.request_id) {
        return {
          call: 'expandAuthorization',
          args: [{
            client_id: who.client_id,
            request_id: who.runtime.request_id,
            decision: 'grant',
          }],
        };
      }
      return {
        call: 'approveAuthorizationV2',
        args: [buildApproveBody({
          clientId: who.client_id,
          realmId, realmIds, grants, scopeFor, links, expiresAt, groupId, redirectUri, toYaml,
          encUserPrivkey, faceId, showAllFaces, placement,
        })],
      };

    // AN AGENT IS ITS OWN ACT, AND ITS OWN ENDPOINT.
    //
    // This shared the `app` case and so was broken twice over. It posted to
    // `/authorize/approve`, which does not read `sub_entity_key` at all — it only
    // ever filters for EPs where that column IS NULL — so a success there would
    // have attached the realm to the PARENT APP rather than to the agent, and
    // left the agent still unassigned with nothing raised. And it fell back to
    // `parent_app_id`, an internal UUID, when the preview carried no `client_id`;
    // every grant route looks an app up BY client_id, so that lookup found no row
    // and the whole flow answered 404 APP_NOT_FOUND.
    //
    // `/authorize/sub-entity` is the route built for this. Two differences from
    // approve that are not guessable: it takes the access blob as `consent`, NOT
    // `consent_yaml`, and it has no `redirect_uri` — the page hands the app back
    // itself via appCallbackUrl(), which is why none is sent.
    case 'sub_entity':
      return {
        call: 'approveSubEntityRealm',
        args: [buildSubEntityBody({
          clientId: who.client_id,
          subEntityKey: who.sub_entity_key,
          realmId, realmIds, grants, scopeFor, links, expiresAt, groupId,
        })],
      };

    case 'user': {
      // A share, not an authorization. `use_pair_realm` is how a single-note
      // share asks the server to file it in the pair's own realm — naming one
      // of your realms would promise everything that realm covers.
      //
      // AN EMPTY BLOB IS OMITTED, NEVER SENT. `toYaml({})` is the STRING "{}",
      // which reaches the server as a present-but-empty consent — and
      // `update_sharing` assigns it wholesale, erasing a live grant. That is how
      // a Full Access share became NO NODE ACCESS with all thirteen verbs
      // stripped in one update, and why re-sharing rewrote the same emptiness.
      // Omitting the key leaves stored consent alone and lets `create_sharing`
      // fall back to its node-scoped read-only default.
      const blob = buildConsentBlob(grants, { scopeFor, links });
      const body = {
        shared_with_user_id: who.id,
        ...(toYaml && Object.keys(blob).length ? { consent_yaml: toYaml(blob) } : {}),
        ...(preview?.item
          ? { use_pair_realm: true, node_id: preview.item.id }
          : { privacy_realm_id: realmId, privacy_realm_ids: realmIds }),
        // ALWAYS SENT, null included. `update_sharing` changes the expiry only when
        // the key is present, so omitting it for "Never expires" left the old date:
        // extending an expired friend to Never reported success and stayed expired.
        // ("Keep" arrives here as that date, so it is unchanged.)
        expires_at: expiresAt || null,
        // Who they see you as, when this screen settled it — the same two fields
        // the app body carries. Not stored by `create_sharing` yet; sent so the
        // answer exists on the wire the day it is.
        ...(faceId ? { profile_node_id: faceId } : {}),
        ...(showAllFaces ? { shows_all_identities: true } : {}),
      };
      // A STOPPED SHARE IS HISTORY, NOT SOMETHING TO EDIT. The preview hands back
      // the pair's latest EP even when it was revoked, so the page can start from
      // what you had before. But `update_sharing` only finds live shares, so editing
      // a revoked one 404'd as "Sharing not found". Sharing again is a new grant;
      // the old one stays a record. A PAUSED share is still the pair's one live share
      // (a second is refused), so it is edited — and `resume` brings it back, because
      // approving here is choosing to share again.
      const existing = preview?.existing;
      const live = existing?.ep_id && existing.state !== 'revoked';
      return live
        ? { call: 'updateSharing',
            args: [existing.ep_id, existing.state === 'paused' ? { ...body, resume: true } : body] }
        : { call: 'createSharing', args: [body] };
    }

    case 'follow':
      // Accepting a request to connect is answering it, not granting an app.
      return {
        call: 'respondToFollow',
        args: [who.follow_id, {
          action: 'accept',
          privacy_realm_id: realmId,
          share: grants?.length ? true : undefined,
        }],
      };

    // TWO ACTS, TWO ENDPOINTS. With an invitation this is JOINING, and the call that
    // creates the membership is `…/invitations/<id>/authorize` — `respondToInvitation`
    // only records the answer and points back here, so the page used to loop without
    // ever joining. Without one you are already a member setting what you contribute;
    // sending `respondToInvitation(null, …)` for that crashed the server. The picked
    // realms go with both, and none is a valid answer.
    case 'group':
      return extra.invitationId
        ? { call: 'authorizeGroupInvitation',
            args: [extra.invitationId, { privacyRealmIds: realmIds || [] }] }
        : { call: 'authorizeGroupMembership',
            args: [who.id, { privacyRealmIds: realmIds || [] }] };

    // THESE NAMES ARE THE API'S, NOT MINE. `updatePublicProfile` and
    // `updateGroupPublicProfile` do not exist — the client has `setPublicProfile`
    // (positional: realm id, then selections) and `setGroupPublicProfile`. Every
    // test here passed because the api mock is a Proxy that manufactures whatever
    // name is asked for, so it proved the dispatch, never the destination.
    case 'public':
      return groupId
        ? {
          call: 'setGroupPublicProfile',
          args: [groupId, { privacy_realm_id: realmId, privacy_realm_ids: realmIds }],
        }
        : {
          call: 'setPublicProfile',
          args: [realmId, { privacy_realm_ids: realmIds }],
        };

    case 'realm':
      return {
        call: 'shareNodeToRealms',
        args: [preview?.item?.id, realmIds || [], null, groupId || null],
      };

    // A note into a GROUP. No realm is named, for the reason the 'user' case
    // above gives: naming one of your realms would promise the group everything
    // that realm covers, and the act is this one note. The note joins the
    // `-> <group>` realm on the contract membership already gave you.
    case 'group_share':
      return {
        call: 'shareNodeWithGroup',
        args: [preview?.item?.id, who.id, nodeShareAccess(grants)],
      };

    // ONE PARKED PLUGIN RUN, allowed (the page that draws it is GrantRoute's
    // PluginRunRoute; this is the older page's path, for a server that has no
    // request list). It goes where the Plugins section's Allow and this page's
    // Decline go: requestsApi (pages/plugins/data/api.js), not the app api —
    // `client` names it, and GrantRoute calls `call` on that.
    case 'plugin':
      return {
        client: 'requests',
        call: 'approve',
        args: [who.id, who.request_id, who.group?.id || groupId || null],
      };

    default:
      // Better a loud refusal than a POST to the wrong endpoint.
      throw new Error(`consent: no submit path for counterparty "${kind}"`);
  }
}
